"use client";

import { ArrowRight, Check } from "lucide-react";
import { useState } from "react";
import { CheckoutDialog } from "@/components/checkout_dialog";
import { PricingCardWatermark } from "@/components/pricing/pricing_card_artistic";
import { highlightPricingTextFeatures } from "@/components/pricing/pricing_text_highlight";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import type { PricingRecord } from "@/lib/pricing-data";
import { toCheckoutPlan } from "./checkout_launch";

interface AuditPricingCardProps {
	name: string;
	price: number;
	currency: string;
	features: string[];
	popular: boolean;
	onGetStarted: () => void;
	description?: string;
}

function AuditPricingCard({
	name,
	price,
	currency,
	features,
	popular,
	onGetStarted,
	description,
}: AuditPricingCardProps) {
	const isINR = currency === "inr";
	const currencySymbol = isINR ? "₹" : "$";

	return (
		<div className="group relative isolate h-full bg-white rounded-3xl p-8 border border-zinc-100 shadow-xl flex flex-col overflow-visible">
			<PricingCardWatermark variant="clipboard" />

			{/* Header */}
			<div className="relative z-10 mb-8">
				<h3 className="font-syne font-bold text-lg uppercase text-zinc-400 mb-2 tracking-wide">
					{name}
				</h3>
				<div className="flex items-baseline gap-1">
					<span className="font-jost font-bold text-4xl text-zinc-900 tracking-tight">
						{currencySymbol}
						{price.toLocaleString()}
					</span>
				</div>
				{description && (
					<p className="font-inter text-xs font-medium text-zinc-400 mt-2 uppercase tracking-wider">
						{description}
					</p>
				)}
			</div>

			{/* Features */}
			<div className="relative z-10 flex-1 mb-8 space-y-4">
				{(features ?? []).map((feature) => (
					<div key={feature} className="flex items-start gap-3 group/item">
						<div className="mt-1 w-4 h-4 rounded-full bg-zinc-50 flex items-center justify-center">
							<Check className="w-3 h-3 text-zinc-900" />
						</div>
						<span className="font-inter text-sm text-zinc-600 leading-tight group-hover/item:text-zinc-900 transition-colors">
							{highlightPricingTextFeatures(feature)}
						</span>
					</div>
				))}
			</div>

			{/* CTA */}
			<Button
				type="button"
				onClick={onGetStarted}
				className={`relative z-10 w-full h-12 rounded-xl font-syne font-bold uppercase tracking-wide transition-all duration-300
					${
						popular
							? "bg-red-600 text-white hover:bg-red-700"
							: "bg-zinc-900 text-white hover:bg-red-600"
					}
				`}
			>
				<span>Select Plan</span>
				<ArrowRight className="w-4 h-4 ml-2" />
			</Button>
		</div>
	);
}

export default function AuditPricingTab({
	data,
	country = "usd",
}: {
	data: PricingRecord[];
	country?: string;
}) {
	const [selectedPlan, setSelectedPlan] = useState<PricingRecord | null>(null);
	const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);

	const handleGetStarted = (plan: PricingRecord) => {
		setSelectedPlan(plan);
		setIsCheckoutOpen(true);
	};

	return (
		<>
			<CheckoutDialog
				open={isCheckoutOpen}
				onOpenChange={setIsCheckoutOpen}
				plan={selectedPlan ? toCheckoutPlan(selectedPlan) : null}
				currency={country === "inr" ? "inr" : "usd"}
			/>

			<section className="w-full mb-12">
				<div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
					{(data ?? []).map((pkg) => {
						return (
							<div key={pkg.id} className="h-full">
								<AuditPricingCard
									name={pkg.tier || pkg.name}
									price={
										country === "inr" ? pkg.price_in_inr : pkg.price_in_usd
									}
									currency={country}
									features={pkg.features}
									popular={pkg.popular}
									onGetStarted={() => handleGetStarted(pkg)}
									description="Channel Analysis"
								/>
							</div>
						);
					})}
				</div>
			</section>

			<div className="w-full max-w-3xl mx-auto mb-20">
				<div className="text-center mb-12">
					<h2 className="font-syne font-black text-3xl uppercase tracking-tight text-zinc-900">
						Audit FAQ
					</h2>
					<div className="h-1 w-12 bg-zinc-200 mx-auto mt-4 rounded-full" />
				</div>

				<Accordion type="single" collapsible className="w-full space-y-4">
					<AccordionItem
						value="item-1"
						className="border border-zinc-200 rounded-2xl px-4 overflow-hidden bg-white/50 hover:bg-white transition-colors"
					>
						<AccordionTrigger className="font-syne font-bold text-lg text-zinc-700 hover:text-zinc-900 hover:no-underline py-6">
							What&apos;s included in a Basic Audit?
						</AccordionTrigger>
						<AccordionContent className="font-inter text-zinc-500 leading-relaxed pb-6">
							{highlightPricingTextFeatures(
								"A Basic Audit covers your channel's fundamental metrics including SEO optimization, thumbnail analysis, title effectiveness, and basic growth recommendations.",
							)}
						</AccordionContent>
					</AccordionItem>

					<AccordionItem
						value="item-2"
						className="border border-zinc-200 rounded-2xl px-4 overflow-hidden bg-white/50 hover:bg-white transition-colors"
					>
						<AccordionTrigger className="font-syne font-bold text-lg text-zinc-700 hover:text-zinc-900 hover:no-underline py-6">
							What additional value does Advanced Audit provide?
						</AccordionTrigger>
						<AccordionContent className="font-inter text-zinc-500 leading-relaxed pb-6">
							{highlightPricingTextFeatures(
								"The Advanced Audit includes everything in Basic plus deep competitor analysis, content strategy roadmap, audience retention breakdown, revenue optimization, and a personalized 1-on-1 consultation call.",
							)}
						</AccordionContent>
					</AccordionItem>

					<AccordionItem
						value="item-3"
						className="border border-zinc-200 rounded-2xl px-4 overflow-hidden bg-white/50 hover:bg-white transition-colors"
					>
						<AccordionTrigger className="font-syne font-bold text-lg text-zinc-700 hover:text-zinc-900 hover:no-underline py-6">
							How long does an audit take?
						</AccordionTrigger>
						<AccordionContent className="font-inter text-zinc-500 leading-relaxed pb-6">
							{highlightPricingTextFeatures(
								"Basic Audits are delivered within 48-72 hours. Advanced Audits take 5-7 business days due to the comprehensive analysis and strategy development involved.",
							)}
						</AccordionContent>
					</AccordionItem>

					<AccordionItem
						value="item-4"
						className="border border-zinc-200 rounded-2xl px-4 overflow-hidden bg-white/50 hover:bg-white transition-colors"
					>
						<AccordionTrigger className="font-syne font-bold text-lg text-zinc-700 hover:text-zinc-900 hover:no-underline py-6">
							Is this a one-time service?
						</AccordionTrigger>
						<AccordionContent className="font-inter text-zinc-500 leading-relaxed pb-6">
							{highlightPricingTextFeatures(
								"Yes, audits are one-time services. However, we recommend getting a follow-up audit every 3-6 months to track progress and adapt to algorithm changes.",
							)}
						</AccordionContent>
					</AccordionItem>
				</Accordion>
			</div>
		</>
	);
}
